// NPC strategy implementations for automated gameplay

import type { AnswerCard, TreatmentCard } from "../types/game";
import type { NpcStrategy, StrategyType } from "../types/npc";

class RandomStrategy implements NpcStrategy {
  name: StrategyType = "random";

  selectAnswerCard(options: AnswerCard[]): AnswerCard {
    if (options.length === 0) {
      throw new Error("No answer cards available");
    }
    const index = Math.floor(Math.random() * options.length);
    return options[index];
  }

  selectTreatmentCards(hand: TreatmentCard[], count: number): number[] {
    if (hand.length < count) {
      throw new Error(
        `Not enough cards in hand: have ${hand.length}, need ${count}`,
      );
    }

    const indices: number[] = [];
    const availableIndices = hand.map((_, i) => i);

    for (let i = 0; i < count; i++) {
      const randomIdx = Math.floor(Math.random() * availableIndices.length);
      indices.push(availableIndices[randomIdx]);
      availableIndices.splice(randomIdx, 1);
    }

    return indices.sort((a, b) => a - b);
  }

  pickWinningSubmission(submissions: Record<string, TreatmentCard[]>): string {
    const playerIds = Object.keys(submissions);
    if (playerIds.length === 0) {
      throw new Error("No submissions available");
    }
    const index = Math.floor(Math.random() * playerIds.length);
    return playerIds[index];
  }
}

class SequentialStrategy implements NpcStrategy {
  name: StrategyType = "sequential";

  selectAnswerCard(options: AnswerCard[]): AnswerCard {
    if (options.length === 0) {
      throw new Error("No answer cards available");
    }
    return options[0];
  }

  selectTreatmentCards(hand: TreatmentCard[], count: number): number[] {
    if (hand.length < count) {
      throw new Error(
        `Not enough cards in hand: have ${hand.length}, need ${count}`,
      );
    }

    const indices: number[] = [];
    for (let i = 0; i < count; i++) {
      indices.push(i);
    }
    return indices;
  }

  pickWinningSubmission(submissions: Record<string, TreatmentCard[]>): string {
    const playerIds = Object.keys(submissions).sort();
    if (playerIds.length === 0) {
      throw new Error("No submissions available");
    }
    return playerIds[0];
  }
}

const strategies: Record<StrategyType, NpcStrategy> = {
  random: new RandomStrategy(),
  sequential: new SequentialStrategy(),
};

export function createStrategy(type: StrategyType): NpcStrategy {
  const strategy = strategies[type];
  if (!strategy) {
    throw new Error(`Unknown strategy type: ${type}`);
  }
  return strategy;
}

export { RandomStrategy, SequentialStrategy };
